import { Attachment, AttachmentFile } from '@billy/design-system'
import qs from 'qs'

import { EInvoiceListFilter } from '@features/eInvoicing/constants/eInvoiceListFilter'

import { Origin } from '../../enums/origin'
import { SortDirection } from '../../enums/sortDirection'
import { FileWithOrigin } from '../../types/fileWithOrigin'
import { Metable } from '../../types/metable'
import { UploadFileHeaders } from '../../types/uploadFilesHeaders'
import { deleteRequest, getRequest, postFileRequest, postRequest, postV2Request, putRequest } from '../../utils'
import type { EInvoiceLifecycleAction } from '../eInvoicing/types/eInvoiceLifecycleAction'
import { AttachmentFillType } from './enums/attachmentFillType'
import { AttachmentFilterType } from './enums/attachmentFiltertype'
import { AttachmentsSortProperty } from './enums/attachmentsSortProperty'
import { AttachmentZipDownloadState } from './enums/attachmentZipDownloadState'

export interface AttachmentsResponseData extends Metable {
  attachments: Attachment[]
}

export interface FetchAttachmentsOptions {
  eInvoiceFilter?: EInvoiceListFilter
  include?: string
  isDuplicate?: boolean
  organizationId: string
  page?: number
  pageSize?: number
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: AttachmentsSortProperty
  fillType?: AttachmentFillType
  type?: AttachmentFilterType
}

export const fetchAttachments = ({
  eInvoiceFilter,
  include = 'attachment.file:embed,file.variants:embed',
  isDuplicate,
  organizationId,
  page,
  pageSize,
  searchQuery,
  sortDirection,
  sortProperty,
  fillType,
  type,
}: FetchAttachmentsOptions): Promise<AttachmentsResponseData> => {
  const queryParams = qs.stringify({
    organizationId,
    ...(fillType === AttachmentFillType.Filled ? { handled: 1 } : {}),
    ...(fillType === AttachmentFillType.Nonfilled ? { unhandled: 1 } : {}),
    ...(type !== AttachmentFilterType.All ? { type } : {}),
    ...(eInvoiceFilter ? { eInvoiceFilter } : {}),
    ...(include ? { include } : {}),
    ...(isDuplicate ? { isDuplicate: 1 } : {}),
    ...(page ? { page } : {}),
    ...(pageSize ? { pageSize } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(sortDirection ? { sortDirection } : {}),
    ...(searchQuery ? { q: searchQuery } : {}),
  })

  return getRequest(`/v2/attachments?${queryParams}`)
}

interface FetchAttachmentResponseData {
  attachment: Attachment
}

interface FetchAttachmentOptions {
  id: string
  include?: string
}

export const fetchAttachment = ({
  id,
  include = 'attachment.file:embed,file.variants:embed',
}: FetchAttachmentOptions): Promise<FetchAttachmentResponseData> => {
  const queryParams = qs.stringify({
    ...(include ? { include } : {}),
  })

  return getRequest(`/v2/attachments/${id}?${queryParams}`)
}

interface FetchFileResponseData extends Metable {
  file: AttachmentFile
}

export const fetchFile = (fileId: string): Promise<FetchFileResponseData> => getRequest(`/v2/files/${fileId}`)

interface FileAttachment extends Attachment {
  id: string
  fileId: string
}

export interface UploadFileResponseData {
  files: AttachmentFile[]
  attachments: FileAttachment[]
}

export const uploadFile = (file: File, apiHeaders?: UploadFileHeaders): Promise<UploadFileResponseData> => {
  const updatedFile: FileWithOrigin = file
  updatedFile.origin = Origin.Web

  return postFileRequest('/v2/files', updatedFile, apiHeaders ? { headers: apiHeaders } : {})
}

export interface UpdateAttachmentPayload extends Partial<Omit<Attachment, 'id'>> {
  id: string
}

export const updateAttachment = (payload: UpdateAttachmentPayload): Promise<AttachmentsResponseData> => {
  const { id, ...attachment } = payload
  return putRequest(`/v2/attachments/${id}`, { attachment })
}

interface RegisteredForEInvoicing {
  id: number
  networkId: string
  registeredAt?: string
}

export interface GetRegisteredForEInvoicingPayload {
  organizationId?: string
}

export interface GetRegisteredForEInvoicingResponseData {
  data?: RegisteredForEInvoicing[]
}

export const getRegisteredForEInvoicing = ({
  organizationId,
}: GetRegisteredForEInvoicingPayload): Promise<GetRegisteredForEInvoicingResponseData> => {
  return getRequest(`/integrations/sproom/organizations/${organizationId}/registrations`)
}

interface RegisterForEInvoicingPayload {
  cvr: string
  organizationId?: string
  organizationName?: string
}

export const registerForEInvoicing = ({
  cvr,
  organizationId,
  organizationName,
}: RegisterForEInvoicingPayload): Promise<void> => {
  return postRequest(`/integrations/sproom/organizations/${organizationId}/registrations/nemhandel`, {
    cvr,
    identifierType: 'CVR',
    organizationName,
  })
}

export const unregisterForEInvoicing = ({ organizationId }: { organizationId: string }): Promise<void> => {
  return deleteRequest(`/integrations/sproom/organizations/${organizationId}/registrations/nemhandel`)
}

export interface FetchAttachmentsCountsResponseData extends Metable {
  counts: {
    unhandledAttachments: string
    unhandledBills: string
    unhandledEDocuments: string
    unhandledScoutReceipts: string
  }
}

export const fetchAttachmentsCount = (organizationId: string): Promise<FetchAttachmentsCountsResponseData> =>
  getRequest(`/v2/attachments/counts?organizationId=${organizationId}`)

export const keepDuplicate = (id: string): Promise<void> => postRequest(`/v2/attachments/${id}/keep-duplicate`, {})

export const deleteAttachmentsBulk = (attachmentIds: string[]): Promise<void> => {
  const query = qs.stringify({ ids: attachmentIds })
  return deleteRequest(`/v2/attachments?${query}`)
}
const LIFECYCLE_ENDPOINT_SUFFIX: Record<EInvoiceLifecycleAction, string> = {
  approve: 'approve',
  refuse: 'reject',
  dispute: 'dispute',
  suspend: 'suspend',
}

interface LifecycleActionPayload {
  record: {
    reason?: string
    reasonCode?: string
  }
}

export interface SubmitLifecycleActionProps {
  action: EInvoiceLifecycleAction
  attachmentId?: string
  organizationId?: string
  reason?: string
  reasonCode?: string
}

export const submitLifecycleAction = ({
  action,
  attachmentId,
  organizationId,
  reason,
  reasonCode,
}: SubmitLifecycleActionProps) => {
  const suffix = LIFECYCLE_ENDPOINT_SUFFIX[action]
  const payload: LifecycleActionPayload = action === 'approve' ? { record: {} } : { record: { reason, reasonCode } }

  return postV2Request<LifecycleActionPayload>({
    endpoint: `/v2/organizations/${organizationId}/attachments/${attachmentId}/${suffix}`,
    payload,
  })
}

export interface AttachmentZipDownload {
  id: string
  organizationId: string
  createdTime: string
  updatedTime: string | null
  state: AttachmentZipDownloadState
  errorMessage: string | null
  downloadUrl: string | null
  startedTime: string | null
  completedTime: string | null
}

export interface CreateAttachmentZipDownloadResponseData extends Metable {
  attachmentZipDownloads: AttachmentZipDownload[]
}

interface CreateAttachmentZipDownloadPayload {
  record: {
    attachmentIds: string[]
  }
}

export interface CreateAttachmentZipDownloadProps {
  attachmentIds: string[]
  organizationId: string
}

export const createAttachmentZipDownload = ({
  attachmentIds,
  organizationId,
}: CreateAttachmentZipDownloadProps): Promise<CreateAttachmentZipDownloadResponseData> => {
  return postV2Request<CreateAttachmentZipDownloadPayload, CreateAttachmentZipDownloadResponseData>({
    endpoint: `/v2/organizations/${organizationId}/attachments/create-zip`,
    payload: { record: { attachmentIds } },
  })
}

export interface FetchAttachmentZipDownloadResponseData extends Metable {
  attachmentZipDownload: AttachmentZipDownload
}

export const fetchAttachmentZipDownload = (id: string): Promise<FetchAttachmentZipDownloadResponseData> => {
  return getRequest(`/v2/attachmentZipDownloads/${id}`)
}

export interface RemoveAttachmentOwnerReferencePayload {
  attachmentId: string
  organizationId: string
}

export const removeAttachmentOwnerReference = (
  payload: RemoveAttachmentOwnerReferencePayload,
): Promise<AttachmentsResponseData> => {
  const { attachmentId, organizationId } = payload
  return putRequest(`/v2/attachments/${attachmentId}/removeOwner?organizationId=${organizationId}`, {})
}
